import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { ConfigSection } from '../config-section';
import { Observable, forkJoin, Subject, empty, of } from 'rxjs';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { take, takeWhile, tap, catchError, finalize, switchMap } from 'rxjs';
import { TransactionType, InvoiceLoggingAndSuggestedCoding } from '@admin/models/system-configuration-model';
import { SuggestedCodingHttpService } from '@admin/services/suggested-coding-http.service';
import { TransactionTypeHttpService } from '@admin/services/transaction-type-http.service';
import { ConfirmationService } from 'primeng/api';
import { UserSettingsService } from '@feature/user-settings/services/user-settings.service';
import { MojoWellKnownNames } from '@common/constants/mojo-well-known-names';

@Component({
  selector: 'mojo-mojo-suggested-coding',
  templateUrl: './suggested-coding.component.html'
})
export class SuggestedCodingComponent extends ConfigSection<InvoiceLoggingAndSuggestedCoding> {

  transactionTypes: TransactionType[];
  resetButtonEnabled: boolean;
  lookupTableRefreshInProgress = false;
  lookTableRefreshComplete = false;
  userHasAccessToApprovalHandling: Boolean = false;

  private submitSubject = new Subject<boolean>();

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly userSettings: UserSettingsService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly confirmationService: ConfirmationService,
    private readonly transactionTypeService: TransactionTypeHttpService,
    private readonly suggestedCodingService: SuggestedCodingHttpService) {
    super();
  }

  initialiseConfig(): Observable<InvoiceLoggingAndSuggestedCoding> {
    return forkJoin(
      this.userSettings.userHasAccessToAddOn(MojoWellKnownNames.AddOnCodes.WS003_FormsApprovalHandling),
      this.transactionTypeService.GetTransactionTypes(),
      this.suggestedCodingService.getInvoiceLoggingAndSuggestedCoding()
    ).pipe(switchMap(
      ([hasAccessToApprovalHandling, transactionTypes, config]:
        [Boolean, TransactionType[], InvoiceLoggingAndSuggestedCoding]) => {
        this.userHasAccessToApprovalHandling = hasAccessToApprovalHandling;
        this.transactionTypes = transactionTypes;
        return of(config);
      }));
  }

  createForm(config: InvoiceLoggingAndSuggestedCoding): void {
    this.form = this.formBuilder.group({
      DefaultCreditorInvoiceDocumentTypeCode: [config.DefaultCreditorInvoiceDocumentTypeCode],
      AllowSuggestedCoding: new UntypedFormControl(this.userHasAccessToApprovalHandling && config.AllowSuggestedCoding),
      AnalysisPeriod: new UntypedFormControl(config.AnalysisPeriod, [Validators.required]),
      CreditorPropertyThreshold: new UntypedFormControl(config.CreditorPropertyThreshold, [Validators.required]),
      ExpenseAnalysisThreshold: new UntypedFormControl(config.ExpenseAnalysisThreshold, [Validators.required]),
      ExpenseCodeThreshold: new UntypedFormControl(config.ExpenseCodeThreshold, [Validators.required]),
    });

    if (!this.userHasAccessToApprovalHandling) {
      this.form.get('AllowSuggestedCoding').disable();
    }

    this.form.get('AllowSuggestedCoding').valueChanges.pipe(
      takeWhile(() => this.alive)
    ).subscribe(value => {
      this.updateFormState();
    });

    this.updateFormState();
  }

  submit(): Observable<boolean> {
    const config = this.form.getRawValue() as InvoiceLoggingAndSuggestedCoding;
    if (!config.AllowSuggestedCoding) {
      // The values below will be null when AllowSuggestedCoding is disabled.
      // Send default values instead of nulls to the server otherwise the backend validation will fail.
      config.AnalysisPeriod = 18;
      config.CreditorPropertyThreshold = 1;
      config.ExpenseAnalysisThreshold = 2;
      config.ExpenseCodeThreshold = 5;
    }

    this.confirmationService.confirm({
      key: 'refreshLookupTableConfirmDialog',
      accept: () => this.save(config, true),
      reject: () => this.save(config, false)
    });

    return this.submitSubject.asObservable();
  }

  save(config: InvoiceLoggingAndSuggestedCoding, refereshLookupTable: boolean): void {
    const requests = [this.suggestedCodingService.saveInvoiceLoggingAndSuggestedCoding(config)];
    if (refereshLookupTable) {
      requests.push(this.suggestedCodingService.refreshInvoiceSuggestedCodingResponse());
      this.lookupTableRefreshInProgress = true;
    }
    forkJoin(requests).pipe(
      take(1),
      tap(results => {
        const success = results.filter(x => x == true).length >= results.length;
        if (!refereshLookupTable) {
          this.saveComplete(success);
        } else {
          this.lookTableRefreshComplete = true;
          this.changeDetector.detectChanges();
        }
      }),
      finalize(() => this.lookupTableRefreshInProgress = false),
      catchError(err => {
        this.submitSubject.error(err);
        return empty();
      })
    ).subscribe();
  }

  saveComplete(success: boolean) {
    this.submitSubject.next(success);
    this.submitSubject.complete();
  }

  resetToDefaults(): void {
    const defaultTransactionType = this.transactionTypes.find(x => x.Code === 'PIN');
    this.form.get('DefaultCreditorInvoiceDocumentTypeCode').setValue(defaultTransactionType);
    if (this.userHasAccessToApprovalHandling) {
      this.form.get('AllowSuggestedCoding').setValue(true);
      this.form.get('AnalysisPeriod').setValue(18);
      this.form.get('CreditorPropertyThreshold').setValue(1);
      this.form.get('ExpenseAnalysisThreshold').setValue(2);
      this.form.get('ExpenseCodeThreshold').setValue(3);
    }
    this.form.markAsDirty();
  }

  updateFormState(): void {
    const config = this.form.getRawValue() as InvoiceLoggingAndSuggestedCoding;
    const allowSuggestedCoding = this.userHasAccessToApprovalHandling && config.AllowSuggestedCoding;

    if (allowSuggestedCoding) {
      this.form.get('AnalysisPeriod').enable();
      this.form.get('CreditorPropertyThreshold').enable();
      this.form.get('ExpenseAnalysisThreshold').enable();
      this.form.get('ExpenseCodeThreshold').enable();

      this.form.get('AnalysisPeriod').setValue(this.originalConfig.AnalysisPeriod);
      this.form.get('CreditorPropertyThreshold').setValue(this.originalConfig.CreditorPropertyThreshold);
      this.form.get('ExpenseAnalysisThreshold').setValue(this.originalConfig.ExpenseAnalysisThreshold);
      this.form.get('ExpenseCodeThreshold').setValue(this.originalConfig.ExpenseCodeThreshold);

      this.resetButtonEnabled = true;
    } else {
      this.form.get('AnalysisPeriod').disable();
      this.form.get('CreditorPropertyThreshold').disable();
      this.form.get('ExpenseAnalysisThreshold').disable();
      this.form.get('ExpenseCodeThreshold').disable();

      this.form.get('AnalysisPeriod').setValue(null);
      this.form.get('CreditorPropertyThreshold').setValue(null);
      this.form.get('ExpenseAnalysisThreshold').setValue(null);
      this.form.get('ExpenseCodeThreshold').setValue(null);

      this.resetButtonEnabled = true;
    }
    this.changeDetector.detectChanges();
  }

  invalid(cntrlName: string): boolean {
    const cntrl = this.form.get(cntrlName);
    return cntrl ? cntrl.invalid : false;
  }
}
